'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { getUkrainianPluralForm } from '@/lib/i18n/pluralization';
import { ArrowRight, CalendarDays } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

interface OrganizerCardData {
  id: string;
  name: string;
  slug: string | null;
  description: string | null;
  image_url: string | null;
  eventCount: number;
}

export default function OrganizersDirectoryPage() {
  const { t, language } = useLanguage();
  const supabase = createClient();

  const [organizers, setOrganizers] = useState<OrganizerCardData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      // Embedded count is filtered to published, public, non-cancelled content;
      // profiles without any such content are hidden from the directory.
      const { data, error } = await supabase
        .from('organizer_profiles')
        .select('id, name, slug, description, image_url, content(count)')
        .eq('content.status', 'published')
        .is('content.cancelled_at', null)
        .not('content.is_private', 'is', true);

      if (!error && data) {
        const cards = data
          .map((row) => ({
            id: row.id,
            name: row.name,
            slug: row.slug,
            description: row.description,
            image_url: row.image_url,
            eventCount: (row.content as unknown as { count: number }[])[0]?.count ?? 0,
          }))
          .filter((card) => card.eventCount > 0)
          .sort(
            (a, b) => b.eventCount - a.eventCount || a.name.localeCompare(b.name, 'uk')
          );
        setOrganizers(cards);
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const eventsLabel = (count: number): string => {
    if (language === 'uk') {
      const form = getUkrainianPluralForm(count);
      return t(`organizersPage.events_${form}`);
    }
    return count === 1 ? t('organizersPage.events_one') : t('organizersPage.events_many');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">
          {t('organizersPage.title')}
        </h1>
        <p className="text-sm text-gray-600 mb-8">{t('organizersPage.subtitle')}</p>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
          </div>
        ) : organizers.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-500">{t('organizersPage.empty')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {organizers.map((org) => {
              const initials = org.name
                .split(/\s+/)
                .map((w) => w[0])
                .join('')
                .slice(0, 2)
                .toUpperCase();
              return (
                <Link
                  key={org.id}
                  href={`/organizers/${org.slug || org.id}`}
                  className="group bg-white rounded-xl shadow-sm border border-gray-200 p-5 flex flex-col hover:shadow-md hover:border-blue-200 transition-all"
                >
                  <div className="flex items-start gap-4 mb-3">
                    {org.image_url ? (
                      /* eslint-disable-next-line @next/next/no-img-element */
                      <img
                        src={org.image_url}
                        alt={org.name}
                        className="w-14 h-14 rounded-xl object-cover border border-gray-200 flex-shrink-0"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-lg font-bold text-blue-600">{initials}</span>
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <h2 className="text-sm font-semibold text-gray-900 leading-snug group-hover:text-blue-700 transition-colors">
                        {org.name}
                      </h2>
                      <span className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-gray-500">
                        <CalendarDays className="h-3.5 w-3.5" />
                        {org.eventCount} {eventsLabel(org.eventCount)}
                      </span>
                    </div>
                  </div>
                  {org.description && (
                    <div className="text-xs text-gray-500 leading-relaxed line-clamp-3 [&_p]:mb-1 [&_p:last-child]:mb-0">
                      <ReactMarkdown allowedElements={['p', 'em', 'strong']} unwrapDisallowed>
                        {org.description}
                      </ReactMarkdown>
                    </div>
                  )}
                </Link>
              );
            })}
          </div>
        )}

        {/* Cross-link for prospective organizers who landed here from old links */}
        <div className="mt-10 bg-white rounded-xl border border-gray-200 shadow-sm p-6 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1">
            <h2 className="text-base font-semibold text-gray-900">
              {t('organizersPage.becomeTitle')}
            </h2>
            <p className="text-sm text-gray-600 mt-1">{t('organizersPage.becomeText')}</p>
          </div>
          <Link
            href="/for-organizers"
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 transition-colors flex-shrink-0"
          >
            {t('organizersPage.becomeCta')}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
